import { useRef, useState, type FormEvent } from "react";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useSeo } from "@/hooks/useSeo";

const FORM_ADDR = "https://readdy.ai/api/form/db9jpic6oi3g3tv8v040";
const FORM_ID = "contact-feedback-form";

const contactChannels = [
  { icon: "ri-mail-line", label: "编辑部邮箱", value: "editor@duyang.example", note: "内容纠错与转载授权" },
  { icon: "ri-weibo-fill", label: "官方微博", value: "@抖阳DUYANG", note: "每日更新与后台互动" },
  { icon: "ri-wechat-fill", label: "微信公众号", value: "抖阳DUYANG", note: "每周推送精选长文" },
  { icon: "ri-map-pin-line", label: "编辑部地址", value: "上海市静安区 · 示例地址", note: "来访请提前预约" },
];

export default function ContactPage() {
  const formRef = useRef<HTMLFormElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [statusMessage, setStatusMessage] = useState("");
  const [messageLength, setMessageLength] = useState(0);

  useSeo({
    title: "联系反馈 - 抖阳",
    description:
      "联系抖阳编辑部：内容纠错、转载授权、品牌合作或读者反馈，都可以通过在线表单或邮箱与我们联系。",
    keywords: "抖阳联系,反馈表单,内容合作,编辑部邮箱",
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;

    const form = event.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get("company_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setStatusMessage("感谢反馈，我们已经收到你的留言。");
      form.reset();
      setMessageLength(0);
      return;
    }

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key === "company_alt") return;
      if (typeof value === "string" && value.trim() !== "") payload.append(key, value.trim());
    });

    setSubmitting(true);
    setStatus("idle");
    setStatusMessage("");

    try {
      const response = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        setStatusMessage("感谢反馈，我们已经收到你的留言，会在三个工作日内回复。");
        form.reset();
        setMessageLength(0);
      } else {
        setStatus("error");
        setStatusMessage(serverMsg || "提交失败，请稍后重试，或直接发送邮件到 editor@duyang.example。");
      }
    } catch {
      setStatus("error");
      setStatusMessage("网络异常，提交未成功。请稍后重试，或直接发送邮件到 editor@duyang.example。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100/70 px-4 pb-8 pt-5 md:px-6 md:pb-10">
          <div className="mx-auto max-w-[1400px]">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "联系反馈" }]} />
            <div className="mt-5 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div>
                <p className="font-label text-[11px] uppercase tracking-[0.24em] text-primary-600">Contact</p>
                <h1 className="mt-2 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-4xl">
                  联系与反馈
                </h1>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
                  发现事实错误、想申请转载授权，或者只是想告诉我们哪里可以做得更好——都可以通过下面的方式找到我们。
                </p>
              </div>
              <p className="flex items-center gap-2 text-xs text-foreground-500">
                <i className="ri-time-line text-primary-500" />
                工作日 10:00 – 19:00 回复
              </p>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[1400px] px-4 py-8 md:px-6 md:py-12">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-5">
              <h2 className="font-heading text-lg font-bold text-foreground-950">联系方式</h2>
              <p className="mt-2 text-xs leading-relaxed text-foreground-500">
                以下联系方式为站点演示信息，正式上线前可由编辑部替换为真实信息。
              </p>
              <ul className="mt-5 space-y-3">
                {contactChannels.map((item) => (
                  <li
                    key={item.label}
                    className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                      <i className={`${item.icon} text-base`} />
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs text-foreground-400">{item.label}</p>
                      <p className="mt-0.5 break-words text-sm font-medium text-foreground-900">{item.value}</p>
                      <p className="mt-0.5 text-[11px] text-foreground-400">{item.note}</p>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mt-4 rounded-lg border border-background-200 bg-background-100/70 p-4">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground-950">
                  <i className="ri-shield-check-line text-primary-500" />
                  反馈处理说明
                </h3>
                <ul className="mt-2.5 space-y-2 text-xs leading-relaxed text-foreground-600">
                  <li>1. 事实纠错会在核实后 48 小时内处理，并在原文标注更正。</li>
                  <li>2. 转载授权请注明媒体名称、文章标题与发布渠道。</li>
                  <li>3. 商业合作由编辑部统一评估，不接受影响内容独立性的条件。</li>
                </ul>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <div className="flex flex-wrap items-end justify-between gap-2 border-b border-background-200 pb-4">
                  <div>
                    <h2 className="font-heading text-lg font-bold text-foreground-950">在线反馈表单</h2>
                    <p className="mt-1 text-xs text-foreground-500">带 * 的为必填项，反馈内容不超过 500 字。</p>
                  </div>
                  <span className="flex items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] text-secondary-900">
                    <i className="ri-lock-line" />
                    仅用于回复联系
                  </span>
                </div>

                {status === "success" ? (
                  <div className="mb-4 mt-4 flex items-start gap-3 rounded-md border border-primary-200 bg-primary-50 p-4">
                    <i className="ri-checkbox-circle-fill mt-0.5 text-primary-600" />
                    <div>
                      <p className="text-sm font-medium text-foreground-900">感谢反馈</p>
                      <p className="mt-1 text-xs leading-relaxed text-foreground-600">{statusMessage}</p>
                    </div>
                  </div>
                ) : null}

                {status === "error" ? (
                  <div className="mb-4 mt-4 flex items-start gap-3 rounded-md border border-accent-200 bg-accent-50 p-4">
                    <i className="ri-error-warning-fill mt-0.5 text-accent-600" />
                    <div>
                      <p className="text-sm font-medium text-foreground-900">提交未成功</p>
                      <p className="mt-1 text-xs leading-relaxed text-foreground-600">{statusMessage}</p>
                    </div>
                  </div>
                ) : null}

                <form
                  ref={formRef}
                  id={FORM_ID}
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="mt-5 space-y-4"
                >
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="contact-name" className="mb-1.5 block text-xs text-foreground-600">
                        姓名 / 称呼 <span className="text-primary-600">*</span>
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        maxLength={40}
                        placeholder="怎么称呼你"
                        className="w-full rounded-md border border-background-300 bg-background-100/60 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="mb-1.5 block text-xs text-foreground-600">
                        邮箱 <span className="text-primary-600">*</span>
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        maxLength={80}
                        placeholder="用于接收回复"
                        className="w-full rounded-md border border-background-300 bg-background-100/60 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50 focus:outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="contact-topic" className="mb-1.5 block text-xs text-foreground-600">
                      反馈类型
                    </label>
                    <select
                      id="contact-topic"
                      name="topic"
                      defaultValue="内容纠错"
                      className="w-full cursor-pointer rounded-md border border-background-300 bg-background-100/60 px-3 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:bg-background-50 focus:outline-none"
                    >
                      <option value="内容纠错">内容纠错</option>
                      <option value="转载授权">转载授权</option>
                      <option value="品牌合作">品牌合作</option>
                      <option value="投稿自荐">投稿自荐</option>
                      <option value="产品建议">产品建议</option>
                      <option value="其他问题">其他问题</option>
                    </select>
                  </div>

                  <div>
                    <div className="mb-1.5 flex items-center justify-between">
                      <label htmlFor="contact-message" className="text-xs text-foreground-600">
                        反馈内容 <span className="text-primary-600">*</span>
                      </label>
                      <span className={`text-[11px] ${messageLength > 500 ? "text-primary-600" : "text-foreground-400"}`}>
                        {messageLength} / 500
                      </span>
                    </div>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={6}
                      onChange={(event) => setMessageLength(event.target.value.length)}
                      placeholder="请描述具体问题、相关文章标题或合作需求，越具体越好。"
                      className="w-full resize-y rounded-md border border-background-300 bg-background-100/60 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50 focus:outline-none"
                    />
                  </div>

                  <div className="form-guard-field" aria-hidden="true">
                    <input
                      type="text"
                      name="company_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>

                  <div className="flex flex-col gap-3 border-t border-background-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-[11px] leading-relaxed text-foreground-400">
                      提交即表示你同意我们仅将信息用于本次回复，不会用于营销推送。
                    </p>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {submitting ? (
                        <>
                          <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-background-50/40 border-t-background-50" />
                          提交中
                        </>
                      ) : (
                        <>
                          提交反馈
                          <i className="ri-send-plane-line" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}